import React, { useState, useEffect } from "react";
import {
  Download,
  CheckCircle,
  XCircle,
  Clock,
  Trash2,
  RefreshCw,
  FileText,
  Calendar,
  Loader,
  FolderDown,
} from "lucide-react";
import {
  requestCampaignLogs,
  getDownloadTasks,
  deleteDownloadTask,
} from "../apis/downloadCenter";
import { toast } from "react-toastify";
import { formatTimestampDetailed } from "../utils/timestampUtils";
import { motion } from "framer-motion";

const DownloadCenter = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadTasks();
    const interval = setInterval(() => {
      if (filter === "all" || filter === "in_progress") {
        loadTasks(true);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [filter]);

  const loadTasks = async (silent = false) => {
    try {
      if (!silent) {
        setLoading(true);
      } else {
        setRefreshing(true);
      }

      const params = {};
      if (filter !== "all") {
        params.status = filter;
      }

      const response = await getDownloadTasks(params);
      setTasks(response.data.data || []);
    } catch (error) {
      console.error("Error loading download tasks:", error);
      if (!silent) {
        toast.error("Failed to load download tasks");
      }
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  const handleDeleteTask = async (taskId) => {
    if (!window.confirm("Are you sure you want to delete this download task?")) {
      return;
    }

    try {
      await deleteDownloadTask(taskId);
      toast.success("Download task deleted successfully");
      loadTasks();
    } catch (error) {
      console.error("Error deleting download task:", error);
      toast.error(
        error.response?.data?.error || "Failed to delete download task"
      );
    }
  };

  const handleDownload = (fileUrl, fileName) => {
    if (!fileUrl) {
      toast.error("File is not ready yet");
      return;
    }

    window.open(fileUrl, "_blank");
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "completed":
        return <CheckCircle size={16} className="text-emerald-600" />;
      case "failed":
        return <XCircle size={16} className="text-red-600" />;
      case "in_progress":
        return <Loader size={16} className="text-blue-600 animate-spin" />;
      default:
        return <Clock size={16} className="text-gray-600" />;
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      completed: {
        bg: "bg-emerald-50",
        text: "text-emerald-700",
        border: "border-emerald-200",
        dot: "bg-emerald-500",
      },
      failed: {
        bg: "bg-red-50",
        text: "text-red-700",
        border: "border-red-200",
        dot: "bg-red-500",
      },
      in_progress: {
        bg: "bg-blue-50",
        text: "text-blue-700",
        border: "border-blue-200",
        dot: "bg-blue-500",
      },
    };
    return badges[status] || {
      bg: "bg-gray-50",
      text: "text-gray-700",
      border: "border-gray-200",
      dot: "bg-gray-500",
    };
  };

  const filteredTasks = tasks;

  return (
    <div className="min-h-screen bg-[#fff] p-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-4">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-blue-50 rounded-xl border border-blue-200">
                <FolderDown size={24} className="text-blue-600" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Download Center</h1>
                <p className="text-sm text-gray-600 mt-0.5 font-medium">
                  Manage your campaign log downloads
                </p>
              </div>
            </div>
            <motion.button
              onClick={() => loadTasks()}
              disabled={refreshing}
              className="inline-flex items-center gap-2 px-4 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 disabled:opacity-50 transition-all shadow-sm"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <RefreshCw size={18} className={refreshing ? "animate-spin" : ""} />
              Refresh
            </motion.button>
          </div>

          {/* Stats Overview */}
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-3 mb-4">
            <div className="bg-white rounded-2xl border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold text-gray-600 mb-1">Total Tasks</p>
                  <p className="text-2xl font-bold text-gray-900">{tasks.length}</p>
                </div>
                <div className="p-2 bg-gray-100 rounded-xl border border-gray-200">
                  <FileText size={20} className="text-gray-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold text-gray-600 mb-1">In Progress</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {tasks.filter((t) => t.status === "in_progress").length}
                  </p>
                </div>
                <div className="p-2 bg-blue-50 rounded-xl border border-blue-200">
                  <Loader size={20} className="text-blue-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold text-gray-600 mb-1">Completed</p>
                  <p className="text-2xl font-bold text-emerald-600">
                    {tasks.filter((t) => t.status === "completed").length}
                  </p>
                </div>
                <div className="p-2 bg-emerald-50 rounded-xl border border-emerald-200">
                  <CheckCircle size={20} className="text-emerald-600" />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-gray-200 p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold text-gray-600 mb-1">Failed</p>
                  <p className="text-2xl font-bold text-red-600">
                    {tasks.filter((t) => t.status === "failed").length}
                  </p>
                </div>
                <div className="p-2 bg-red-50 rounded-xl border border-red-200">
                  <XCircle size={20} className="text-red-600" />
                </div>
              </div>
            </div>
          </div>

          {/* Filter Tabs */}
          <div className="ping-pill-tabs">
            <button
              onClick={() => setFilter("all")}
              className={`ping-ptab ${filter === "all" ? "on" : ""}`}
            >
              All ({tasks.length})
            </button>
            <button
              onClick={() => setFilter("in_progress")}
              className={`ping-ptab ${filter === "in_progress" ? "on" : ""}`}
            >
              In Progress ({tasks.filter((t) => t.status === "in_progress").length})
            </button>
            <button
              onClick={() => setFilter("completed")}
              className={`ping-ptab ${filter === "completed" ? "on" : ""}`}
            >
              Completed ({tasks.filter((t) => t.status === "completed").length})
            </button>
            <button
              onClick={() => setFilter("failed")}
              className={`ping-ptab ${filter === "failed" ? "on" : ""}`}
            >
              Failed ({tasks.filter((t) => t.status === "failed").length})
            </button>
          </div>
        </div>

        {/* Tasks Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {loading ? (
            <div className="col-span-full bg-white rounded-2xl border border-gray-200 p-12 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-2xl mb-4 border border-gray-200">
                <Loader className="w-8 h-8 animate-spin text-blue-600" />
              </div>
              <p className="text-sm text-gray-600 font-semibold">Loading download tasks...</p>
            </div>
          ) : filteredTasks.length === 0 ? (
            <div className="col-span-full bg-white rounded-2xl border border-gray-200 p-12 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4 border border-gray-200">
                <FileText className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">
                No download tasks found
              </h3>
              <p className="text-sm text-gray-600 font-medium">
                {filter !== "all"
                  ? `No ${filter.replace("_", " ")} tasks available`
                  : "Request a campaign log download to get started"}
              </p>
            </div>
          ) : (
            filteredTasks.map((task, index) => {
              const statusBadge = getStatusBadge(task.status);

              return (
                <motion.div
                  key={task.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05, duration: 0.3 }}
                  className="bg-white rounded-2xl border border-gray-200 hover:border-gray-300 hover:shadow-md transition-all overflow-hidden"
                >
                  {/* Card Header */}
                  <div className="bg-gradient-to-r from-gray-50 to-gray-100 px-4 py-3 border-b border-gray-200">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="flex-1 min-w-0">
                        <h3 className="text-sm font-bold text-gray-900 truncate">
                          {task.file_name || "Campaign Logs"}
                        </h3>
                      </div>
                      <span
                        className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold border ${statusBadge.bg} ${statusBadge.text} ${statusBadge.border}`}
                      >
                        <span className={`w-2 h-2 rounded-full ${statusBadge.dot} animate-pulse`}></span>
                        {task.status.replace("_", " ")}
                      </span>
                    </div>
                    {task.campaign_name && (
                      <p className="text-xs text-gray-600 truncate font-medium">
                        📊 Campaign: <span className="font-semibold">{task.campaign_name}</span>
                      </p>
                    )}
                  </div>

                  {/* Card Body */}
                  <div className="p-4">
                    {/* Status Section */}
                    <div className="mb-4">
                      {task.status === "in_progress" && (
                        <div className="flex items-center gap-2 p-3 bg-blue-50 border border-blue-200 rounded-xl">
                          <Loader size={16} className="text-blue-600 animate-spin flex-shrink-0" />
                          <p className="text-xs text-blue-700 font-bold">
                            Generating file...
                          </p>
                        </div>
                      )}

                      {task.status === "completed" && (
                        <div className="flex items-center gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-xl">
                          <CheckCircle size={16} className="text-emerald-600 flex-shrink-0" />
                          <p className="text-xs text-emerald-700 font-bold">
                            File ready to download
                          </p>
                        </div>
                      )}

                      {task.status === "failed" && task.error_message && (
                        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-xl">
                          <XCircle size={16} className="text-red-600 flex-shrink-0 mt-0.5" />
                          <div className="flex-1 min-w-0">
                            <p className="text-xs text-red-700 font-bold mb-1">
                              Failed
                            </p>
                            <p className="text-xs text-red-600 break-words font-medium">
                              {task.error_message}
                            </p>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Timestamps */}
                    <div className="space-y-2 mb-4 pb-4 border-b border-gray-200">
                      <div className="flex items-center gap-2 text-xs text-gray-600">
                        <div className="p-1.5 bg-gray-100 rounded-lg border border-gray-200">
                          <Calendar size={12} className="text-gray-600" />
                        </div>
                        <div>
                          <span className="font-bold">Created:</span>
                          <span className="ml-1.5 font-medium">
                            {new Date(task.created_at).toLocaleString()}
                          </span>
                        </div>
                      </div>
                      {task.updated_at !== task.created_at && (
                        <div className="flex items-center gap-2 text-xs text-gray-600">
                          <div className="p-1.5 bg-gray-100 rounded-lg border border-gray-200">
                            <Clock size={12} className="text-gray-600" />
                          </div>
                          <div>
                            <span className="font-bold">Updated:</span>
                            <span className="ml-1.5 font-medium">
                              {new Date(task.updated_at).toLocaleString()}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      {task.status === "completed" && task.file_url && (
                        <motion.button
                          onClick={() => handleDownload(task.file_url, task.file_name)}
                          className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-bold text-gray-900 rounded-xl transition-all shadow-sm hover:shadow-md border border-gray-300"
                          style={{ backgroundColor: "#eefa94" }}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                        >
                          <Download size={16} />
                          Download
                        </motion.button>
                      )}
                      <motion.button
                        onClick={() => handleDeleteTask(task.id)}
                        className={`${
                          task.status === "completed" && task.file_url
                            ? "p-2.5"
                            : "flex-1 inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-bold"
                        } text-red-600 hover:bg-red-50 rounded-xl transition-all border border-red-200 hover:border-red-300`}
                        title="Delete Task"
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                      >
                        <Trash2 size={16} />
                        {!(task.status === "completed" && task.file_url) && "Delete"}
                      </motion.button>
                    </div>
                  </div>
                </motion.div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default DownloadCenter;
